//Created By Cursor
import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Avatar,
  IconButton,
  Button,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  School as SchoolIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarMonth as CalendarIcon,
  ReceiptLong as OrdersIcon,
  Payments as PaymentsIcon,
  Inventory2 as ProductsIcon,
  AccountBalanceWallet as WalletIcon,
  Call as CallIcon,
  WhatsApp as WhatsAppIcon,
  Refresh as RefreshIcon,
  MoreTime as ExtendDemoIcon,
  Apps as AppAccessIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  Login as LoginIcon,
  Cancel as CancelSubIcon,
  Autorenew as SubscriptionIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";
import environment from "../config/environment";

//Created By Cursor — e.g. 1 Oct 2025
const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (value) => {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
};

// Created By Cursor
const normalizeDialPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.startsWith("91") && digits.length >= 12) return digits;
  return digits;
};

const STATUS_TONE = {
  billed: crmColors.success.main,
  active: crmColors.success.main,
  pending: crmColors.warning.main,
  demo: crmColors.info.main,
  expired: crmColors.error.main,
  "service expired": crmColors.error.main,
  "demo expired": crmColors.error.main,
  "not billed": crmColors.secondary.main,
  inactive: crmColors.secondary.main,
  //Created By Cursor — Grace uses red (same as All Schools / Search)
  grace: crmColors.error.main,
};

// Created By Cursor — match All Schools: Grace · X days left / Demo · …
const getGraceDaysLeft = (school) => {
  if (!school) return null;
  if (school.grace_days_left != null && !Number.isNaN(Number(school.grace_days_left))) {
    return Math.max(0, Number(school.grace_days_left));
  }
  if (!school.grace_end_date) return null;
  const end = new Date(school.grace_end_date).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.floor((end - Date.now()) / 86400000));
};

const formatGraceLabel = (schoolOrSummary) => {
  const left = getGraceDaysLeft(schoolOrSummary);
  if (left == null) return "Grace";
  if (left <= 0) return "Grace · Last day";
  return `Grace · ${left} day${left === 1 ? "" : "s"} left`;
};

const getSchoolStatusLabel = (school) => {
  if (!school) return "—";
  const s = String(school.status || "").toLowerCase();
  if (s === "grace") return formatGraceLabel(school);
  if (s === "expired") return "Service Expired";
  if (school.demo_label) {
    if (school.demo_expired || /expired/i.test(String(school.demo_label))) {
      return "Demo expired";
    }
    if (s === "demo" || school.demo_days_left != null) {
      return `Demo · ${school.demo_label}`;
    }
  }
  return school.status || "—";
};

const getBillingStatusLabel = (summary, school) => {
  const raw = String(summary?.billing_status || "").trim();
  if (!raw) return "—";
  if (/^grace$/i.test(raw)) {
    return formatGraceLabel({
      grace_days_left: summary?.grace_days_left ?? school?.grace_days_left,
      grace_end_date: summary?.grace_end_date ?? school?.grace_end_date,
    });
  }
  if (/^expired$/i.test(raw)) return "Service Expired";
  return raw;
};

const resolveProductStatusName = (product) => {
  const name = String(product?.product_status_name || "").trim();
  if (name) return name;
  const ps = Number(product?.product_status);
  if (ps === 1) return "Demo";
  if (ps === 2) return "Active";
  if (ps === 3) return "InActive";
  return "";
};

// Created By Cursor — Products list: entitlement overrides Active when Grace / Service Expired
const getProductStatusLabel = (product) => {
  if (!product) return "—";
  const entitlement = String(product.display_status || "").toLowerCase();
  if (entitlement === "grace") return formatGraceLabel(product);
  if (entitlement === "expired") return "Service Expired";

  const statusName = resolveProductStatusName(product);
  if (/^grace$/i.test(statusName)) return formatGraceLabel(product);
  if (/^expired$/i.test(statusName)) return "Service Expired";

  const isDemo =
    Number(product.product_status) === 1 || /^demo$/i.test(statusName);
  if (isDemo && product.demo_label) {
    if (product.demo_expired || /expired/i.test(String(product.demo_label))) {
      return "Demo expired";
    }
    return `Demo · ${product.demo_label}`;
  }
  return statusName || "—";
};

//Created By Cursor — subscription row status label
const getSubscriptionRowStatusLabel = (sub) => {
  if (!sub) return "—";
  const st = String(sub.status || sub.subscription_status || "").trim();
  const lower = st.toLowerCase();
  if (lower === "grace") {
    return formatGraceLabel({
      grace_days_left: sub.grace_days_left,
      grace_end_date: sub.grace_end_date,
      grace_days: sub.grace_days,
    });
  }
  if (lower === "expired") return "Expired";
  if (lower === "cancelled") return "Cancelled";
  if (lower === "upgraded") return "Upgraded";
  if (lower === "active") return "Active";
  if (lower === "demo" || lower === "trial") return st;
  // derive from dates when status blank
  if (sub.service_end_date) {
    const now = Date.now();
    const endTs = new Date(sub.service_end_date).getTime();
    const graceEnd = sub.grace_end_date
      ? new Date(sub.grace_end_date).getTime()
      : endTs + (Number(sub.grace_days) || 7) * 86400000;
    if (graceEnd < now) return "Expired";
    if (endTs <= now && graceEnd >= now) {
      return formatGraceLabel({
        grace_end_date: sub.grace_end_date,
        grace_days: sub.grace_days,
      });
    }
    return "Active";
  }
  return st || "—";
};

//Created By Cursor — cancel only live / expired billed subs (not Cancelled/Upgraded)
const isCancellableSubscription = (sub) => {
  if (!sub?.subscription_id) return false;
  const st = String(sub.status || "").toLowerCase();
  return ["active", "grace", "expired", ""].includes(st);
};

//Created By Cursor — accent color for subscription status cards
const getSubscriptionAccent = (sub) => {
  const label = String(getSubscriptionRowStatusLabel(sub) || "").toLowerCase();
  if (label.startsWith("grace") || label === "expired") return crmColors.error.main;
  if (label === "cancelled") return crmColors.text.secondary;
  if (label === "upgraded") return crmColors.info.main;
  if (label === "active") return crmColors.success.main;
  return crmColors.primary.main;
};

//Created By Cursor — Active product accounts (billed Active / Demo operational)
const isActiveProductRow = (p) => {
  if (!p) return false;
  const ps = Number(p.product_status);
  const name = String(p.product_status_name || "").toLowerCase();
  if (ps === 2 || name === "active" || name === "grace" || name === "expired") return true;
  if (ps === 1 || name === "demo") return true;
  return false;
};

const shellSx = {
  borderRadius: 2.5,
  border: `1px solid ${crmColors.border.default}`,
  boxShadow: "0 6px 18px -14px rgba(15, 31, 47, 0.22)",
  overflow: "hidden",
  background: crmColors.background.paper,
  "&::before": { display: "none" },
};

// Created By Cursor — shared L/R inset for every section on this page
const SECTION_PX = { xs: 2, md: 2.5 };

const StatusChip = memo(function StatusChip({ label }) {
  const raw = String(label || "");
  const key = raw.toLowerCase();
  let color = STATUS_TONE[key] || crmColors.info.main;
  if (/cancelled/i.test(key)) color = crmColors.text.secondary;
  if (/upgraded/i.test(key)) color = crmColors.info.main;
  if (/^demo\s*[·.]/i.test(raw)) {
    color = /last demo/i.test(raw) ? crmColors.warning.main : crmColors.info.main;
  }
  if (/demo expired/i.test(raw)) color = crmColors.error.main;
  //Created By Cursor — Grace / Service Expired always red (incl. "Grace · N days left")
  if (/^grace\b/i.test(raw) || /service expired/i.test(raw) || /^expired$/i.test(raw)) {
    color = crmColors.error.main;
  }
  return (
    <Chip
      label={label || "—"}
      size="small"
      sx={{
        height: 26,
        fontWeight: 800,
        fontSize: "0.72rem",
        color: "#fff",
        bgcolor: color,
        textTransform: "none",
        borderRadius: "8px",
        maxWidth: 240,
      }}
    />
  );
});

const SummaryTile = memo(function SummaryTile({ label, value, color, icon }) {
  return (
    <Box
      sx={{
        p: 1.75,
        height: "100%",
        borderRadius: "14px",
        border: `1px solid ${alpha(color, 0.28)}`,
        background: `linear-gradient(145deg, ${alpha(color, 0.1)} 0%, #fff 70%)`,
        display: "flex",
        alignItems: "center",
        gap: 1.35,
      }}
    >
      <Avatar
        sx={{
          width: 42,
          height: 42,
          bgcolor: color,
          color: "#fff",
          boxShadow: `0 6px 14px ${alpha(color, 0.35)}`,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 800,
            color: crmColors.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: "1.15rem",
            fontWeight: 800,
            color: crmColors.text.primary,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
});

const SectionCard = memo(function SectionCard({ title, icon, count, children, contentSx, headerSx }) {
  return (
    <Card
      elevation={0}
      sx={{
        ...shellSx,
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: SECTION_PX,
          py: 1.35,
          minHeight: 52,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          borderBottom: `1px solid ${crmColors.border.default}`,
          background: `linear-gradient(120deg, ${crmColors.primary.surfaceMuted} 0%, ${alpha(
            crmColors.primary.accent,
            0.08
          )} 100%)`,
          ...headerSx,
        }}
      >
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: crmColors.primary.main,
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>
        <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", lineHeight: 1.2 }}>
          {title}
        </Typography>
        {count != null && (
          <Chip
            label={count}
            size="small"
            sx={{
              ml: "auto",
              height: 22,
              fontWeight: 800,
              borderRadius: "8px",
              bgcolor: alpha(crmColors.primary.main, 0.12),
              color: crmColors.primary.main,
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          px: SECTION_PX,
          py: 0.75,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          ...contentSx,
        }}
      >
        {children}
      </Box>
    </Card>
  );
});

const tableHeadSx = {
  "& th": {
    fontWeight: 800,
    fontSize: 11,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: crmColors.text.secondary,
    bgcolor: alpha(crmColors.primary.main, 0.03),
    borderBottom: `1px solid ${crmColors.border.default}`,
    py: 1.25,
    px: 1.25,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    "&:first-of-type": { pl: 0 },
    "&:last-of-type": { pr: 0 },
  },
};

const tableBodyCellSx = {
  borderColor: crmColors.border.default,
  py: 1.25,
  px: 1.25,
  verticalAlign: "top",
  "&:first-of-type": { pl: 0 },
  "&:last-of-type": { pr: 0 },
};

const EmptyRows = ({ colSpan, text }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        {text}
      </Typography>
    </TableCell>
  </TableRow>
);

const DEMO_EXTEND_PRESETS = [1, 2, 5, 7, 10, 15, 30];
const GRACE_EXTEND_PRESETS = [3, 7, 15, 30];

//Created By Cursor — show Extend Grace when entitlement is Grace or Service Expired
const isGraceExtendableProduct = (p) => {
  const ds = String(p?.display_status || p?.product_status_name || "").toLowerCase();
  return ds === "grace" || ds === "expired";
};

const APP_ACCESS_LOGIN_FILTERS = ["ALL", "Admin", "Clerk", "Teacher"];

// Created By Cursor — soft tone per login type for App Access chips/avatars
const loginTypeTone = (type) => {
  const t = String(type || "").toLowerCase();
  if (t === "admin") return { bg: alpha(crmColors.primary.main, 0.12), fg: crmColors.primary.main };
  if (t === "clerk") return { bg: alpha(crmColors.energy.main, 0.14), fg: crmColors.energy.dark };
  if (t === "teacher") return { bg: alpha(crmColors.success.main, 0.12), fg: crmColors.success.dark };
  return { bg: alpha(crmColors.secondary.main, 0.12), fg: crmColors.secondary.dark };
};

// Created By Cursor — open EduOKWeb /mypage/login with CRM auto-login via URL hash
// (hash is not sent to the server; EduOKWeb scrubs it immediately after reading)
const openManageCrmLogin = (staff, schoolCode) => {
  const userId = staff?.staff_id || "";
  const password = staff?.password || "";
  if (!userId || !password) {
    toast.error("Staff login credentials not available");
    return;
  }
  const base = environment.MANAGE_APP_URL || "https://www.manage.eduokerp.com";
  const hash = new URLSearchParams({
    crmlogin: "1",
    user_id: userId,
    password,
    school_code: schoolCode || "",
  }).toString();
  window.open(`${base}/mypage/login#${hash}`, "_blank", "noopener,noreferrer");
};

// Created By Cursor — Extend Demo only when product_status = 1 (Demo), never for Active (2)
const isDemoProduct = (p) => {
  const status = Number(p?.product_status);
  if (status === 2 || status === 3) return false;
  const name = String(p?.product_status_name || "").toLowerCase().trim();
  if (name === "active" || name === "inactive") return false;
  return status === 1 || name === "demo";
};

const SchoolDetails = () => {
  const { schoolCode } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendProduct, setExtendProduct] = useState("");
  const [extendDays, setExtendDays] = useState(15);
  const [extendRemark, setExtendRemark] = useState("");
  const [extending, setExtending] = useState(false);

  const [graceOpen, setGraceOpen] = useState(false);
  const [graceProduct, setGraceProduct] = useState("");
  const [graceDays, setGraceDays] = useState(7);
  const [graceRemark, setGraceRemark] = useState("");
  const [graceExtending, setGraceExtending] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelProduct, setCancelProduct] = useState("");
  const [cancelSubscriptionId, setCancelSubscriptionId] = useState("");
  const [cancelRemark, setCancelRemark] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const [appAccessOpen, setAppAccessOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [loginTypeFilter, setLoginTypeFilter] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getSchoolDetail(schoolCode);
    if (result.success && result.data) {
      setDetail(result.data);
    } else {
      toast.error(result.error || "Failed to load school details");
      setDetail(null);
    }
    setLoading(false);
  }, [schoolCode]);

  useEffect(() => {
    load();
  }, [load]);

  const school = detail?.school;
  const summary = detail?.summary;
  const products = detail?.products || [];
  const orders = detail?.orders || [];
  const payments = detail?.payments || [];
  const dial = normalizeDialPhone(school?.admin_phone);
  const billingLabel = getBillingStatusLabel(summary, school);
  const statusLabel = getSchoolStatusLabel(school);
  const showStatusChip =
    Boolean(statusLabel) &&
    statusLabel !== "—" &&
    String(statusLabel).toLowerCase() !== String(billingLabel || "").toLowerCase();

  const productNameByCode = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      if (p.product_code) map[p.product_code] = p.product_name || p.product_code;
    });
    return map;
  }, [products]);

  //Created By Cursor — subscriptions list (API) with product names
  const subscriptionRows = useMemo(() => {
    const fromApi = Array.isArray(detail?.subscriptions) ? detail.subscriptions : [];
    if (fromApi.length) {
      return fromApi.map((s) => ({
        ...s,
        product_name: s.product_name || productNameByCode[s.product_code] || s.product_code,
      }));
    }
    // Fallback: build from product entitlement fields
    return products
      .filter((p) => p.subscription_id)
      .map((p) => ({
        subscription_id: p.subscription_id,
        product_code: p.covered_by_product || p.product_code,
        product_name:
          productNameByCode[p.covered_by_product || p.product_code] ||
          p.product_name ||
          p.product_code,
        status: p.subscription_status || p.display_status || "Active",
        service_start_date: p.service_start_date,
        service_end_date: p.service_end_date,
        grace_end_date: p.grace_end_date,
        grace_days: p.grace_days,
        grace_days_left: p.grace_days_left,
        billing_cycle_months: p.billing_cycle_months,
        current_order_id: p.current_order_id,
      }));
  }, [detail?.subscriptions, products, productNameByCode]);

  const activeProducts = useMemo(
    () => products.filter((p) => isActiveProductRow(p) && p.product_code),
    [products]
  );

  const demoProducts = useMemo(
    () => products.filter((p) => isDemoProduct(p) && p.product_code),
    [products]
  );

  const graceProducts = useMemo(
    () => products.filter((p) => isGraceExtendableProduct(p) && p.product_code),
    [products]
  );

  const cancellableSubscriptions = useMemo(
    () => subscriptionRows.filter((s) => isCancellableSubscription(s)),
    [subscriptionRows]
  );

  const schoolInGraceOrExpired = useMemo(() => {
    const s = String(school?.status || summary?.billing_status || "").toLowerCase();
    return s === "grace" || s === "expired";
  }, [school?.status, summary?.billing_status]);

  const filteredStaff = useMemo(() => {
    if (loginTypeFilter === "ALL") return staffList;
    return staffList.filter(
      (s) => String(s.login_type || "").toLowerCase() === loginTypeFilter.toLowerCase()
    );
  }, [staffList, loginTypeFilter]);

  const loginTypeOptions = useMemo(() => {
    const fromData = [
      ...new Set(
        staffList
          .map((s) => String(s.login_type || "").trim())
          .filter((t) => t && t.toLowerCase() !== "none")
      ),
    ];
    const base = APP_ACCESS_LOGIN_FILTERS.filter(
      (t) => t === "ALL" || fromData.some((d) => d.toLowerCase() === t.toLowerCase())
    );
    fromData.forEach((t) => {
      if (!base.some((b) => b.toLowerCase() === t.toLowerCase())) base.push(t);
    });
    return base.length ? base : APP_ACCESS_LOGIN_FILTERS;
  }, [staffList]);

  // Created By Cursor
  const openAppAccess = async () => {
    setAppAccessOpen(true);
    setLoginTypeFilter("ALL");
    setStaffLoading(true);
    const result = await apiService.getSchoolStaff(schoolCode, "ALL");
    setStaffLoading(false);
    if (result.success) {
      setStaffList(Array.isArray(result.data) ? result.data : []);
    } else {
      setStaffList([]);
      toast.error(result.error || "Failed to load staff");
    }
  };

  // Created By Cursor — open extend dialog for a specific product row
  const openExtendDemo = (productCode) => {
    if (!productCode) {
      toast.error("Select a product");
      return;
    }
    setExtendProduct(productCode);
    setExtendDays(15);
    setExtendRemark("");
    setExtendOpen(true);
  };

  // Created By Cursor
  const submitExtendDemo = async () => {
    const days = Number(extendDays);
    if (!extendProduct) {
      toast.error("Select a product");
      return;
    }
    if (!Number.isFinite(days) || days < 1) {
      toast.error("Enter days to extend (min 1)");
      return;
    }
    setExtending(true);
    const result = await apiService.extendSchoolDemo({
      school_code: schoolCode,
      product_code: extendProduct,
      extra_days: Math.min(365, Math.floor(days)),
      remark: String(extendRemark || "").trim(),
    });
    setExtending(false);
    if (result.success) {
      toast.success(result.message || `Demo extended by ${days} day(s) from today`);
      setExtendOpen(false);
      load();
    } else {
      toast.error(result.error || "Failed to extend demo");
    }
  };

  //Created By Cursor — open Cancel Subscription dialog (from subscription row)
  const openCancelSubscription = (subOrProductCode = null) => {
    if (subOrProductCode && typeof subOrProductCode === "object") {
      setCancelSubscriptionId(subOrProductCode.subscription_id || "");
      setCancelProduct(subOrProductCode.product_code || "");
    } else {
      const code =
        subOrProductCode ||
        cancellableSubscriptions[0]?.product_code ||
        "";
      const match = cancellableSubscriptions.find((s) => s.product_code === code);
      setCancelProduct(code);
      setCancelSubscriptionId(match?.subscription_id || "");
    }
    setCancelRemark("");
    setCancelOpen(true);
  };

  const submitCancelSubscription = async () => {
    if (!cancelSubscriptionId && !cancelProduct) {
      toast.error("Select a subscription to cancel");
      return;
    }
    setCancelling(true);
    const result = await apiService.cancelSchoolSubscription({
      school_code: schoolCode,
      product_code: cancelProduct,
      subscription_id: cancelSubscriptionId,
      remark: cancelRemark,
      created_by: "",
    });
    setCancelling(false);
    if (result.success) {
      toast.success(result.message || "Subscription cancelled");
      setCancelOpen(false);
      load();
    } else {
      toast.error(result.error || "Failed to cancel subscription");
    }
  };

  //Created By Cursor — open Extend Grace dialog (product row or header)
  const openExtendGrace = (productCode = "") => {
    const code =
      productCode ||
      graceProducts[0]?.product_code ||
      products.find((p) => String(p.product_code || "").toUpperCase() === "TM001")?.product_code ||
      products[0]?.product_code ||
      "";
    setGraceProduct(code);
    setGraceDays(7);
    setGraceRemark("");
    setGraceOpen(true);
  };

  //Created By Cursor
  const submitExtendGrace = async () => {
    const days = Number(graceDays);
    if (!Number.isFinite(days) || days < 1) {
      toast.error("Enter grace days to extend (min 1)");
      return;
    }
    setGraceExtending(true);
    const result = await apiService.extendSchoolGrace({
      school_code: schoolCode,
      product_code: graceProduct || "",
      extra_days: Math.min(365, Math.floor(days)),
      remark: String(graceRemark || "").trim(),
    });
    setGraceExtending(false);
    if (result.success) {
      const mode = result.data?.mode;
      toast.success(
        result.message ||
          (mode === "restart"
            ? `Grace restarted for ${days} day(s)`
            : `Grace extended by ${days} day(s)`)
      );
      setGraceOpen(false);
      load();
    } else {
      toast.error(result.error || "Failed to extend grace");
    }
  };

  return (
    <PageContentLayout
      title={school?.school_name || "School Details"}
      subtitle={
        school
          ? `${school.school_code}${school.admin_name ? ` · ${school.admin_name}` : ""}`
          : "Account overview"
      }
      darkBar
      breadcrumbs={[
        {
          label: "All Schools",
          icon: <SchoolIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
        { label: school?.school_name || "Details" },
      ]}
      backTo="/schools"
      actions={
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={loading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <RefreshIcon />}
          onClick={load}
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 800,
            background: crmColors.energy.main,
            backgroundImage: "none",
            color: "#fff",
            border: `1px solid ${alpha("#fff", 0.55)}`,
            "&:hover": { background: crmColors.energy.dark, backgroundImage: "none" },
          }}
        >
          Refresh
        </Button>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 10 }}>
          <CircularProgress size={34} />
          <Typography variant="body2" sx={{ color: crmColors.text.secondary }}>
            Loading school details…
          </Typography>
        </Box>
      ) : !detail ? (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <SchoolIcon sx={{ fontSize: 48, color: crmColors.border.hover, mb: 1 }} />
          <Typography sx={{ fontWeight: 800, mb: 0.5 }}>School not found</Typography>
          <Typography variant="body2" color="text.secondary">
            Check the school code and try again.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2} sx={{ width: "100%" }}>
          {/* Hero profile */}
          <Card elevation={0} sx={shellSx}>
            <Box
              sx={{
                px: SECTION_PX,
                py: { xs: 2, md: 2.25 },
                background: `linear-gradient(125deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.mid} 48%, ${crmColors.hero.end} 100%)`,
                color: "#fff",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 58,
                      height: 58,
                      bgcolor: alpha("#fff", 0.18),
                      color: "#fff",
                      border: `2px solid ${alpha("#fff", 0.45)}`,
                      fontWeight: 800,
                      fontSize: 22,
                    }}
                  >
                    {(school.school_name || "S").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "1.1rem", md: "1.25rem" },
                        lineHeight: 1.25,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {school.school_name}
                      {school.school_code ? (
                        <Typography
                          component="span"
                          sx={{ ml: 0.75, fontWeight: 700, fontSize: "0.95rem", opacity: 0.88 }}
                        >
                          ({school.school_code})
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography sx={{ fontSize: 13, opacity: 0.88, mt: 0.35 }}>
                      {[school.school_address, school.district_name, school.state, school.country]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                      <StatusChip label={billingLabel} />
                      {showStatusChip ? <StatusChip label={statusLabel} /> : null}
                    </Stack>
                  </Box>
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {schoolInGraceOrExpired || graceProducts.length > 0 ? (
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      startIcon={<ExtendDemoIcon sx={{ fontSize: 18 }} />}
                      onClick={() => openExtendGrace()}
                      sx={{
                        textTransform: "none",
                        fontWeight: 800,
                        letterSpacing: "0.02em",
                        borderRadius: "12px",
                        height: 42,
                        px: 1.75,
                        color: "#fff",
                        background: `linear-gradient(135deg, ${crmColors.error.main} 0%, ${crmColors.error.dark} 100%)`,
                        border: `1px solid ${alpha("#fff", 0.22)}`,
                        boxShadow: `0 6px 18px ${alpha(crmColors.error.main, 0.35)}`,
                        "&:hover": {
                          background: `linear-gradient(135deg, ${crmColors.error.dark} 0%, #991b1b 100%)`,
                        },
                      }}
                    >
                      Extend Grace
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<AppAccessIcon sx={{ fontSize: 18 }} />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14, opacity: 0.85 }} />}
                    onClick={openAppAccess}
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      letterSpacing: "0.02em",
                      borderRadius: "12px",
                      height: 42,
                      px: 1.75,
                      color: "#fff",
                      background: `linear-gradient(135deg, ${crmColors.energy.main} 0%, ${crmColors.energy.dark} 100%)`,
                      border: `1px solid ${alpha("#fff", 0.22)}`,
                      boxShadow: `0 6px 18px ${alpha(crmColors.energy.main, 0.35)}`,
                      "&:hover": {
                        background: `linear-gradient(135deg, ${crmColors.energy.dark} 0%, #c2410c 100%)`,
                        boxShadow: `0 8px 22px ${alpha(crmColors.energy.main, 0.45)}`,
                      },
                    }}
                  >
                    App Access
                  </Button>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      borderRadius: "12px",
                      bgcolor: alpha("#fff", 0.12),
                      border: `1px solid ${alpha("#fff", 0.28)}`,
                      maxWidth: "100%",
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", opacity: 0.8 }}>
                        ADMIN
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                        {school.admin_name || "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, opacity: 0.9 }}>
                        {school.admin_phone || "—"}
                      </Typography>
                    </Box>
                    {dial ? (
                      <Stack direction="row" spacing={0.35} sx={{ ml: 0.5 }}>
                        <IconButton
                          size="small"
                          component="a"
                          href={`tel:+${dial}`}
                          title="Call"
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: alpha("#fff", 0.18),
                            color: "#fff",
                            "&:hover": { bgcolor: alpha("#fff", 0.28) },
                          }}
                        >
                          <CallIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://wa.me/${dial}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="WhatsApp"
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: alpha("#25D366", 0.35),
                            color: "#fff",
                            "&:hover": { bgcolor: alpha("#25D366", 0.5) },
                          }}
                        >
                          <WhatsAppIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    ) : null}
                  </Box>
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ px: SECTION_PX, py: { xs: 1.75, md: 2 } }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarIcon sx={{ fontSize: 18, color: crmColors.primary.main }} />
                    <Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 800, color: crmColors.text.secondary, textTransform: "uppercase" }}>
                        Registered
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                        {formatDate(school.date_added)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarIcon sx={{ fontSize: 18, color: crmColors.energy.main }} />
                    <Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 800, color: crmColors.text.secondary, textTransform: "uppercase" }}>
                        Valid until
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                        {formatDate(summary?.service_expiry)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationIcon sx={{ fontSize: 18, color: crmColors.primary.main }} />
                    <Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 800, color: crmColors.text.secondary, textTransform: "uppercase" }}>
                        Location
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                        {[school.state, school.country].filter(Boolean).join(", ") || "—"}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon sx={{ fontSize: 18, color: crmColors.primary.main }} />
                    <Box>
                      <Typography sx={{ fontSize: 10, fontWeight: 800, color: crmColors.text.secondary, textTransform: "uppercase" }}>
                        Orders
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                        {summary?.orders_count ?? 0}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>

              {/* KPIs inside school card */}
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: `1px solid ${crmColors.border.default}`,
                }}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <SummaryTile
                      label="Total Ordered"
                      value={formatMoney(summary?.total_ordered)}
                      color={crmColors.info.main}
                      icon={<OrdersIcon fontSize="small" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <SummaryTile
                      label="Total Paid"
                      value={formatMoney(summary?.total_paid)}
                      color={crmColors.success.main}
                      icon={<PaymentsIcon fontSize="small" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <SummaryTile
                      label="Balance Due"
                      value={formatMoney(summary?.balance_due)}
                      color={
                        Number(summary?.balance_due) > 0
                          ? crmColors.error.main
                          : crmColors.secondary.main
                      }
                      icon={<WalletIcon fontSize="small" />}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Card>

          {/* Subscriptions — card list */}
          <SectionCard
            title="Subscriptions"
            icon={<SubscriptionIcon sx={{ fontSize: 16 }} />}
            count={subscriptionRows.length}
          >
            {subscriptionRows.length === 0 ? (
              <Box sx={{ py: 2.5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  No subscriptions yet
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {subscriptionRows.map((s) => {
                  const accent = getSubscriptionAccent(s);
                  const statusLabel = getSubscriptionRowStatusLabel(s);
                  const canCancel = isCancellableSubscription(s);
                  return (
                    <Box
                      key={s.subscription_id || `${s.product_code}-${s.service_end_date}`}
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: "12px",
                        border: `1.5px solid ${alpha(accent, 0.35)}`,
                        background: `linear-gradient(90deg, ${alpha(accent, 0.14)} 0%, ${crmColors.background.paper} 32%)`,
                        boxShadow: `0 2px 10px -6px ${alpha(accent, 0.5)}`,
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 5,
                          bgcolor: accent,
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ px: 1.75, py: 1.35, pl: 2.25 }}
                      >
                        <Avatar
                          sx={{
                            width: 42,
                            height: 42,
                            flexShrink: 0,
                            bgcolor: accent,
                            color: "#fff",
                            fontWeight: 800,
                            fontSize: 13,
                            boxShadow: `0 4px 12px ${alpha(accent, 0.4)}`,
                          }}
                        >
                          {String(s.product_code || "S").replace(/^TM/i, "")}
                        </Avatar>

                        <Box sx={{ flex: "1 1 180px", minWidth: 140 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: 15.5,
                              lineHeight: 1.25,
                              color: crmColors.text.primary,
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {s.product_name || s.product_code}
                          </Typography>
                          <Chip
                            label={s.product_code}
                            size="small"
                            sx={{
                              mt: 0.4,
                              height: 22,
                              fontWeight: 800,
                              fontSize: 11,
                              bgcolor: alpha(crmColors.primary.main, 0.12),
                              color: crmColors.primary.main,
                            }}
                          />
                        </Box>

                        <Chip
                          label={statusLabel}
                          size="small"
                          sx={{
                            height: 30,
                            fontWeight: 800,
                            fontSize: 12,
                            px: 0.5,
                            color: "#fff",
                            bgcolor: accent,
                            borderRadius: "8px",
                            boxShadow: `0 3px 8px ${alpha(accent, 0.35)}`,
                          }}
                        />

                        <Stack spacing={0.35} sx={{ flex: "1 1 220px", minWidth: 180 }}>
                          <Stack direction="row" spacing={0.6} alignItems="center">
                            <CalendarIcon sx={{ fontSize: 16, color: accent }} />
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: 13.5,
                                color: crmColors.text.primary,
                              }}
                            >
                              {s.service_start_date || s.service_end_date
                                ? `${formatDate(s.service_start_date)} → ${formatDate(s.service_end_date)}`
                                : "—"}
                            </Typography>
                          </Stack>
                          {s.grace_end_date &&
                          String(s.status || "").toLowerCase() === "grace" ? (
                            <Typography
                              sx={{ fontWeight: 800, fontSize: 12, color: crmColors.error.main }}
                            >
                              Grace until {formatDate(s.grace_end_date)}
                            </Typography>
                          ) : null}
                        </Stack>

                        {s.billing_cycle_months ? (
                          <Chip
                            label={`${s.billing_cycle_months} mo cycle`}
                            size="small"
                            sx={{
                              height: 28,
                              fontWeight: 800,
                              fontSize: 12,
                              ml: { xs: 0, sm: "auto" },
                              bgcolor: alpha(crmColors.secondary.main, 0.14),
                              color: crmColors.secondary.dark,
                            }}
                          />
                        ) : null}

                        <Box sx={{ flexShrink: 0, ml: s.billing_cycle_months ? 0 : { xs: 0, sm: "auto" } }}>
                          {canCancel ? (
                            <Button
                              size="medium"
                              variant="contained"
                              disableElevation
                              startIcon={<CancelSubIcon sx={{ fontSize: 16 }} />}
                              onClick={() => openCancelSubscription(s)}
                              sx={{
                                textTransform: "none",
                                height: 36,
                                px: 1.75,
                                borderRadius: "10px",
                                fontWeight: 800,
                                fontSize: 13,
                                bgcolor: crmColors.error.main,
                                color: "#fff",
                                boxShadow: `0 4px 12px ${alpha(crmColors.error.main, 0.35)}`,
                                "&:hover": { bgcolor: crmColors.error.dark },
                              }}
                            >
                              Cancel
                            </Button>
                          ) : (
                            <Chip
                              label={
                                String(s.status || "").toLowerCase() === "cancelled"
                                  ? "Cancelled"
                                  : String(s.status || "").toLowerCase() === "upgraded"
                                    ? "Upgraded"
                                    : "—"
                              }
                              size="small"
                              sx={{
                                height: 28,
                                fontWeight: 800,
                                fontSize: 12,
                                bgcolor: alpha(crmColors.text.secondary, 0.12),
                                color: crmColors.text.secondary,
                              }}
                            />
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </SectionCard>

          {/* Active Products */}
          <SectionCard
            title="Active Products"
            icon={<ProductsIcon sx={{ fontSize: 16 }} />}
            count={activeProducts.length}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadSx}>
                    <TableCell>Product</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Demo Window</TableCell>
                    <TableCell>Since</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeProducts.length === 0 ? (
                    <EmptyRows colSpan={5} text="No active product accounts" />
                  ) : (
                    activeProducts.map((p) => (
                      <TableRow
                        key={p.product_code}
                        hover
                        sx={{ "& td": tableBodyCellSx }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {p.product_name || p.product_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.product_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip label={getProductStatusLabel(p)} />
                        </TableCell>
                        <TableCell>
                          {p.demo_start_date
                            ? `${formatDate(p.demo_start_date)} → ${formatDate(p.demo_end_date)}`
                            : "—"}
                        </TableCell>
                        <TableCell>{formatDate(p.date_added)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                            {isDemoProduct(p) ? (
                              <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                startIcon={<ExtendDemoIcon sx={{ fontSize: 15 }} />}
                                onClick={() => openExtendDemo(p.product_code)}
                                sx={{
                                  textTransform: "none",
                                  height: 28,
                                  minHeight: 28,
                                  px: 1.25,
                                  borderRadius: "8px",
                                  fontWeight: 800,
                                  fontSize: 11,
                                  background: crmColors.primary.main,
                                  color: "#fff",
                                  "&:hover": { background: crmColors.primary.dark },
                                }}
                              >
                                Extend Demo
                              </Button>
                            ) : null}
                            {isGraceExtendableProduct(p) ? (
                              <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                startIcon={<ExtendDemoIcon sx={{ fontSize: 15 }} />}
                                onClick={() => openExtendGrace(p.product_code)}
                                sx={{
                                  textTransform: "none",
                                  height: 28,
                                  minHeight: 28,
                                  px: 1.25,
                                  borderRadius: "8px",
                                  fontWeight: 800,
                                  fontSize: 11,
                                  background: crmColors.error.main,
                                  color: "#fff",
                                  "&:hover": { background: crmColors.error.dark },
                                }}
                              >
                                Extend Grace
                              </Button>
                            ) : null}
                            {!isDemoProduct(p) && !isGraceExtendableProduct(p) ? (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <Grid container spacing={2} alignItems="stretch">
            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                display: "flex",
                pl: { xs: 0, lg: 0 },
                ml: { xs: -1, sm: -1.5, md: -2 },
              }}
            >
              <SectionCard
                title="Orders"
                icon={<OrdersIcon sx={{ fontSize: 16 }} />}
                count={orders.length}
                headerSx={{ pl: { xs: 1.5, md: 1.75 } }}
                contentSx={{ pl: { xs: 1.5, md: 1.75 } }}
              >
                <TableContainer sx={{ flex: 1 }}>
                  <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                    <TableHead>
                      <TableRow sx={tableHeadSx}>
                        <TableCell width="28%">Order</TableCell>
                        <TableCell width="14%">Date</TableCell>
                        <TableCell width="14%" align="right">Amount</TableCell>
                        <TableCell width="14%" align="right">Paid</TableCell>
                        <TableCell width="16%">Status</TableCell>
                        <TableCell width="14%">Valid Until</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.length === 0 ? (
                        <EmptyRows colSpan={6} text="No orders yet" />
                      ) : (
                        orders.map((o) => (
                          <TableRow
                            key={o.order_id}
                            hover
                            sx={{ "& td": tableBodyCellSx }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {o.invoice_id || o.order_id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {o.product_name || o.product_code}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatDate(o.order_date)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatMoney(o.total_amount)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: crmColors.success.dark, fontWeight: 700 }}>
                              {formatMoney(o.amount_paid)}
                            </TableCell>
                            <TableCell>
                              <StatusChip label={o.order_status_name} />
                            </TableCell>
                            <TableCell>{formatDate(o.service_expiry)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
            </Grid>

            <Grid
              item
              xs={12}
              lg={6}
              sx={{
                display: "flex",
                pr: { xs: 1.5, sm: 2, md: 3 },
              }}
            >
              <SectionCard
                title="Payments"
                icon={<PaymentsIcon sx={{ fontSize: 16 }} />}
                count={payments.length}
              >
                <TableContainer sx={{ flex: 1 }}>
                  <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                    <TableHead>
                      <TableRow sx={tableHeadSx}>
                        <TableCell width="28%">Transaction</TableCell>
                        <TableCell width="14%">Date</TableCell>
                        <TableCell width="16%">Channel</TableCell>
                        <TableCell width="16%">Status</TableCell>
                        <TableCell width="14%" align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.length === 0 ? (
                        <EmptyRows colSpan={5} text="No payments recorded" />
                      ) : (
                        payments.map((p) => (
                          <TableRow
                            key={p.transaction_id}
                            hover
                            sx={{ "& td": tableBodyCellSx }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {p.transaction_id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {p.payment_mode_name || "—"}
                              </Typography>
                              {p.gateway_transaction_id ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  PG: {p.gateway_transaction_id}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>{formatDate(p.payment_date)}</TableCell>
                            <TableCell>{p.payment_channel || "Manual"}</TableCell>
                            <TableCell>
                              <StatusChip label={p.payment_status_name || p.payment_status} />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: crmColors.success.dark }}>
                              {formatMoney(p.transaction_amount)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
            </Grid>
          </Grid>
        </Stack>
      )}

      {/* Created By Cursor — App Access staff list (filter by login type → manage CRM login) */}
      <Dialog
        open={appAccessOpen}
        onClose={() => !staffLoading && setAppAccessOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow: `0 24px 48px ${alpha("#0f1f2f", 0.22)}`,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            px: 2.5,
            pt: 2.25,
            pb: 2,
            color: "#fff",
            background: `linear-gradient(125deg, ${crmColors.primary.darker} 0%, ${crmColors.hero.mid} 48%, ${crmColors.hero.end} 100%)`,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setAppAccessOpen(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              color: "#fff",
              bgcolor: alpha("#fff", 0.14),
              "&:hover": { bgcolor: alpha("#fff", 0.24) },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha("#fff", 0.16),
                border: `1px solid ${alpha("#fff", 0.28)}`,
              }}
            >
              <AppAccessIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0, pr: 4 }}>
              <Typography sx={{ fontWeight: 900, fontSize: 18, lineHeight: 1.2 }}>
                App Access
              </Typography>
              <Typography sx={{ fontSize: 12.5, opacity: 0.88, mt: 0.25, fontWeight: 600 }}>
                {school?.school_name || schoolCode} · Open manage ERP as staff
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1.75 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              icon={<GroupsIcon sx={{ fontSize: "16px !important", color: "#fff !important" }} />}
              label={`${filteredStaff.length} staff`}
              sx={{
                height: 26,
                fontWeight: 800,
                color: "#fff",
                bgcolor: alpha("#fff", 0.16),
                border: `1px solid ${alpha("#fff", 0.22)}`,
                "& .MuiChip-icon": { ml: 0.6 },
              }}
            />
            <Chip
              size="small"
              icon={<LoginIcon sx={{ fontSize: "15px !important", color: "#fff !important" }} />}
              label="CRM auto-login"
              sx={{
                height: 26,
                fontWeight: 700,
                color: "#fff",
                bgcolor: alpha("#fff", 0.12),
                border: `1px solid ${alpha("#fff", 0.18)}`,
                "& .MuiChip-icon": { ml: 0.6 },
              }}
            />
          </Stack>
        </Box>

        <DialogContent sx={{ px: 2.25, pt: 2, pb: 1.5, bgcolor: crmColors.background.default }}>
          <Box
            sx={{
              display: "flex",
              gap: 0.6,
              p: 0.5,
              mb: 1.75,
              borderRadius: "12px",
              bgcolor: crmColors.background.paper,
              border: `1px solid ${crmColors.border.default}`,
              overflowX: "auto",
            }}
          >
            {loginTypeOptions.map((t) => {
              const active = loginTypeFilter === t;
              const count =
                t === "ALL"
                  ? staffList.length
                  : staffList.filter(
                      (s) => String(s.login_type || "").toLowerCase() === t.toLowerCase()
                    ).length;
              return (
                <Box
                  key={t}
                  component="button"
                  type="button"
                  onClick={() => setLoginTypeFilter(t)}
                  sx={{
                    flex: "1 1 auto",
                    minWidth: 72,
                    border: 0,
                    cursor: "pointer",
                    borderRadius: "9px",
                    py: 0.85,
                    px: 1,
                    fontWeight: 800,
                    fontSize: 12.5,
                    fontFamily: "inherit",
                    transition: "background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease",
                    bgcolor: active ? crmColors.primary.main : "transparent",
                    color: active ? "#fff" : crmColors.text.secondary,
                    boxShadow: active ? `0 4px 12px ${alpha(crmColors.primary.main, 0.28)}` : "none",
                    "&:hover": {
                      bgcolor: active ? crmColors.primary.dark : alpha(crmColors.primary.main, 0.06),
                    },
                  }}
                >
                  {t === "ALL" ? "All" : t}
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      opacity: active ? 0.85 : 0.55,
                      fontWeight: 700,
                      fontSize: 11,
                    }}
                  >
                    {count}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {staffLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 5, gap: 1.25 }}>
              <CircularProgress size={30} thickness={4} />
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: crmColors.text.secondary }}>
                Loading staff…
              </Typography>
            </Box>
          ) : filteredStaff.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4.5,
                px: 2,
                borderRadius: "14px",
                bgcolor: crmColors.background.paper,
                border: `1px dashed ${crmColors.border.default}`,
              }}
            >
              <GroupsIcon sx={{ fontSize: 40, color: crmColors.border.hover, mb: 1 }} />
              <Typography sx={{ fontWeight: 800, color: crmColors.text.primary }}>
                No staff found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 600 }}>
                Try another login type filter
              </Typography>
            </Box>
          ) : (
            <Stack
              spacing={0.85}
              sx={{
                maxHeight: 380,
                overflowY: "auto",
                pr: 0.25,
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: alpha(crmColors.primary.main, 0.25),
                  borderRadius: 8,
                },
              }}
            >
              {filteredStaff.map((staff) => {
                const tone = loginTypeTone(staff.login_type);
                return (
                  <Box
                    key={staff.staff_id}
                    onClick={() => openManageCrmLogin(staff, schoolCode)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.35,
                      p: 1.35,
                      borderRadius: "14px",
                      bgcolor: crmColors.background.paper,
                      border: `1px solid ${crmColors.border.default}`,
                      cursor: "pointer",
                      transition:
                        "border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease",
                      "&:hover": {
                        borderColor: crmColors.border.primaryTint,
                        bgcolor: alpha(crmColors.primary.main, 0.03),
                        boxShadow: `0 8px 20px ${alpha(crmColors.primary.main, 0.1)}`,
                        transform: "translateY(-1px)",
                        "& .app-access-launch": {
                          bgcolor: crmColors.primary.main,
                          color: "#fff",
                        },
                      },
                    }}
                  >
                    <Avatar
                      src={staff.staff_image_path || undefined}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: tone.fg,
                        fontWeight: 800,
                        fontSize: 15,
                        boxShadow: `0 0 0 3px ${tone.bg}`,
                      }}
                    >
                      {(staff.staff_name || "S").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: 14,
                          lineHeight: 1.25,
                          color: crmColors.text.primary,
                        }}
                        noWrap
                      >
                        {staff.staff_name || "—"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: crmColors.text.secondary, display: "block" }}
                        noWrap
                      >
                        {staff.staff_id}
                        {staff.staff_role ? ` · ${staff.staff_role}` : ""}
                        {staff.department_name ? ` · ${staff.department_name}` : ""}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={staff.login_type || "—"}
                      sx={{
                        height: 24,
                        fontWeight: 800,
                        fontSize: 11,
                        bgcolor: tone.bg,
                        color: tone.fg,
                      }}
                    />
                    <Box
                      className="app-access-launch"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "10px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(crmColors.primary.main, 0.08),
                        color: crmColors.primary.main,
                        transition: "background 0.15s ease, color 0.15s ease",
                        flexShrink: 0,
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: crmColors.background.paper,
            borderTop: `1px solid ${crmColors.border.default}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{ flex: 1, fontWeight: 600, color: crmColors.text.secondary, pl: 0.5 }}
          >
            Opens manage.eduokerp.com in a new tab
          </Typography>
          <Button
            onClick={() => setAppAccessOpen(false)}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              borderColor: crmColors.border.default,
              color: crmColors.text.secondary,
              "&:hover": { borderColor: crmColors.border.hover, bgcolor: alpha("#000", 0.02) },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created By Cursor — Cancel Subscription dialog */}
      <Dialog open={cancelOpen} onClose={() => !cancelling && setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Cancel Subscription</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Cancels the live subscription and deactivates the product account. After cancel you can
              create a <strong>New</strong> bill from BillDesk.
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              label="Subscription"
              value={cancelSubscriptionId || cancelProduct}
              onChange={(e) => {
                const val = e.target.value;
                const match = cancellableSubscriptions.find(
                  (s) => s.subscription_id === val || s.product_code === val
                );
                setCancelSubscriptionId(match?.subscription_id || "");
                setCancelProduct(match?.product_code || val);
              }}
              disabled={cancelling || cancellableSubscriptions.length <= 1}
            >
              {(cancellableSubscriptions.length
                ? cancellableSubscriptions
                : subscriptionRows
              ).map((s) => (
                <MenuItem
                  key={s.subscription_id || s.product_code}
                  value={s.subscription_id || s.product_code}
                >
                  {(s.product_name || s.product_code) +
                    (s.subscription_id ? ` · ${s.subscription_id}` : "") +
                    ` · ${getSubscriptionRowStatusLabel(s)}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              label="Remark (optional)"
              value={cancelRemark}
              onChange={(e) => setCancelRemark(e.target.value)}
              disabled={cancelling}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            onClick={() => setCancelOpen(false)}
            disabled={cancelling}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={submitCancelSubscription}
            disabled={cancelling || !cancelProduct}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              bgcolor: crmColors.error.main,
              "&:hover": { bgcolor: crmColors.error.dark },
            }}
          >
            {cancelling ? "Cancelling…" : "Cancel Subscription"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created By Cursor — Extend Grace dialog */}
      <Dialog open={graceOpen} onClose={() => !graceExtending && setGraceOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Extend Grace</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Product"
              value={graceProduct}
              onChange={(e) => setGraceProduct(e.target.value)}
              disabled={graceExtending || (graceProducts.length || products.length) <= 1}
            >
              {(graceProducts.length
                ? graceProducts
                : products.filter((p) => p.product_code)
              ).map((p) => (
                <MenuItem key={p.product_code} value={p.product_code}>
                  {p.product_name || p.product_code}
                  {p.display_status === "Grace"
                    ? ` · ${formatGraceLabel(p)}`
                    : p.display_status === "Expired"
                      ? " · Service Expired"
                      : ""}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1, color: crmColors.text.secondary }}>
                Quick add
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {GRACE_EXTEND_PRESETS.map((d) => (
                  <Chip
                    key={d}
                    label={`${d} days`}
                    clickable
                    onClick={() => setGraceDays(d)}
                    sx={{
                      fontWeight: 800,
                      bgcolor:
                        Number(graceDays) === d
                          ? alpha(crmColors.error.main, 0.14)
                          : alpha(crmColors.text.secondary, 0.08),
                      color:
                        Number(graceDays) === d
                          ? crmColors.error.main
                          : crmColors.text.secondary,
                      border: `1px solid ${
                        Number(graceDays) === d
                          ? crmColors.error.main
                          : crmColors.border.default
                      }`,
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Days"
              value={graceDays}
              onChange={(e) => setGraceDays(e.target.value)}
              inputProps={{ min: 1, max: 365 }}
              disabled={graceExtending}
              helperText="Expired → new grace from today. In grace → adds days to grace end."
            />

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Remark (optional)"
              value={graceRemark}
              onChange={(e) => setGraceRemark(e.target.value)}
              disabled={graceExtending}
              placeholder="Reason for grace extension…"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            onClick={() => setGraceOpen(false)}
            disabled={graceExtending}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitExtendGrace}
            disabled={graceExtending}
            startIcon={
              graceExtending ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <ExtendDemoIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              bgcolor: crmColors.error.main,
              "&:hover": { bgcolor: crmColors.error.dark },
            }}
          >
            {graceExtending ? "Extending…" : "Extend Grace"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created By Cursor — Extend Demo dialog */}
      <Dialog open={extendOpen} onClose={() => !extending && setExtendOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Extend Demo</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Product"
              value={extendProduct}
              onChange={(e) => setExtendProduct(e.target.value)}
              disabled={extending || demoProducts.length <= 1}
            >
              {(demoProducts.length ? demoProducts : products.filter((p) => p.product_code)).map(
                (p) => (
                  <MenuItem key={p.product_code} value={p.product_code}>
                    {p.product_name || p.product_code}
                    {p.demo_label ? ` · ${p.demo_label}` : ""}
                  </MenuItem>
                )
              )}
            </TextField>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1, color: crmColors.text.secondary }}>
                Quick add
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {DEMO_EXTEND_PRESETS.map((d) => (
                  <Chip
                    key={d}
                    label={d === 1 ? "1 day" : `${d} days`}
                    clickable
                    onClick={() => setExtendDays(d)}
                    sx={{
                      fontWeight: 800,
                      bgcolor:
                        Number(extendDays) === d
                          ? alpha(crmColors.primary.main, 0.14)
                          : alpha(crmColors.text.secondary, 0.08),
                      color:
                        Number(extendDays) === d
                          ? crmColors.primary.main
                          : crmColors.text.secondary,
                      border: `1px solid ${
                        Number(extendDays) === d
                          ? crmColors.primary.main
                          : crmColors.border.default
                      }`,
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Days"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              inputProps={{ min: 1, max: 365 }}
              disabled={extending}
              helperText="Expired demo → restarts from today. Active demo → adds days to current period."
            />

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Remark (optional)"
              value={extendRemark}
              onChange={(e) => setExtendRemark(e.target.value)}
              disabled={extending}
              placeholder="Reason for extension…"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button
            onClick={() => setExtendOpen(false)}
            disabled={extending}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitExtendDemo}
            disabled={extending}
            startIcon={
              extending ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <ExtendDemoIcon />
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              bgcolor: crmColors.primary.main,
            }}
          >
            {extending ? "Extending…" : "Extend Demo"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContentLayout>
  );
};

export default SchoolDetails;
